import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { stubRuleSuggestionProvider } from "./ai/StubRuleSuggestionProvider";
import { generateDemoData } from "./data/generateDemoData";
import type { DecisionEvent, DecisionEventOverallStatus, DecisionEventType } from "./domain/decisionEventTypes";
import type { DemoOutputQueue, DemoRule, DemoRuleStatus } from "./domain/ruleTypes";
import type { PriceToleranceRule, TaxonomyProductMapping } from "./domain/types";
import { buildDecisionEventEvaluation } from "./events/buildDecisionEvents";
import {
    applyIntegratedRuleSimplifiedLogicEdit,
    applyIntegratedRuleSuggestion,
    closeIntegratedRuleEditor,
    createInitialIntegratedRuleWorkbenchState,
    createIntegratedRuleWorkbenchViewModel,
    createRule,
    deleteRule,
    duplicateRule,
    getDraftRuleDefinitionForEvaluation,
    isRuleModified,
    openIntegratedRuleEditor,
    reorderRules,
    refreshIntegratedRuleDraftStorage,
    renderDisplayRuleLogicLines,
    resetIntegratedRuleDraft,
    resetIntegratedRuleWorkbench,
    saveIntegratedRuleDraft,
    saveRuleCatalog,
    updateIntegratedRuleDraftDefinition,
    updateIntegratedRuleOutcomeRouting,
    type IntegratedRuleWorkbenchEnvironment,
    type IntegratedRuleWorkbenchViewModel,
} from "./application/integratedRuleWorkbench";
import { describeRuleIntegrationStatus, type RuleSimplifiedLogicEdit } from "./appglueIntegration/ruleIntegrationTypes";
import {
    IntegratedRuleEditorHost,
    isWideIntegratedRuleEditor,
} from "./appglueIntegration/executableHosts/IntegratedRuleEditorHost";
import {
    getRuleLogicDefinition,
    IMPLEMENTATION_LABELS,
    normalizeRuleField,
    type DemoRuleLogicDefinition,
    type RuleLogicCondition,
    type RuleLogicImplementation,
    type RuleLogicOperator,
    type RuleOutcomeRouting,
} from "./rules/ruleLogicDefinitions";

type StatusFilter = "all" | DecisionEventOverallStatus;
type EventTypeFilter = "all" | DecisionEventType;

const ROW_HEIGHT = 58;
const EVENT_VIEWPORT_HEIGHT = 620;
const OVERSCAN_ROWS = 6;
const INTEGRATED_VALIDATION_TEST_IDS = {
    section: "electronics-integrated-rule-validation-preview",
    pass: "electronics-integrated-preview-pass",
    flagged: "electronics-integrated-preview-flagged",
    rejected: "electronics-integrated-preview-rejected",
};

const EVENT_TYPE_LABELS: Record<DecisionEventType, string> = {
    part_enrichment_received: "Part enrichment",
    supplier_price_update_received: "Supplier price update",
    lifecycle_status_change: "Lifecycle change",
    compliance_change_detected: "Compliance change",
};

const IMPLEMENTATION_OPTIONS: RuleLogicImplementation[] = [
    "statement_rule",
    "rule_table",
    "value_table",
    "mapping",
    "text_merge",
    "flow_route",
    "expression",
];

const OPERATOR_OPTIONS: RuleLogicOperator[] = [
    "==",
    "!=",
    "in",
    "not_in",
    ">",
    ">=",
    "<",
    "<=",
    "exists",
    "matches",
    "not_contains",
    "changed",
    "lookup",
];

const OUTPUT_QUEUE_OPTIONS: DemoOutputQueue[] = [
    "published_catalog_candidates",
    "quarantine_records",
    "archived_parts",
    "pending_review",
    "procurement_review_queue",
    "compliance_review_queue",
    "salesforce_product_create_events",
    "salesforce_price_alert_events",
    "salesforce_obsolescence_alert_events",
    "epicor_cost_file_updates",
    "epicor_description_previews",
    "salesforce_product_name_previews",
    "source_override_audit",
];

const formatInteger = (value: number): string => value.toLocaleString();

const statusText = (status: DemoRuleStatus | DecisionEventOverallStatus): string => {
    if (status === "flagged") return "flag";
    if (status === "rejected") return "reject";
    return status;
};

const selectedRuleOutcome = (event: DecisionEvent, ruleId: string) =>
    event.ruleOutcomes.find((outcome) => outcome.ruleId === ruleId && outcome.status !== "skipped");

const statusTone = (
    status: DemoRuleStatus | DecisionEventOverallStatus,
): "success" | "warning" | "danger" | "neutral" => {
    if (status === "pass") return "success";
    if (status === "flagged") return "warning";
    if (status === "rejected" || status === "error") return "danger";
    return "neutral";
};

const eventMatchesSearch = (event: DecisionEvent, search: string): boolean => {
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return [
        event.eventId,
        event.eventType,
        event.subjectId,
        event.subjectLabel,
        event.headline,
        event.primaryRuleId ?? "",
        event.downstreamAction,
    ].some((value) => value.toLowerCase().includes(needle));
};

const contextLabel = (key: string): string => key.replaceAll("_", " ");

const browserWorkbenchEnvironment = (): IntegratedRuleWorkbenchEnvironment =>
    typeof window === "undefined"
        ? {}
        : {
              storage: window.localStorage,
              locationSearch: window.location.search,
          };

const parseFieldList = (value: string): string[] =>
    value
        .split(",")
        .map((field) => normalizeRuleField(field))
        .filter(Boolean);

type IntegratedValidationPanelProps = {
    workbenchView: IntegratedRuleWorkbenchViewModel;
    draftRuleDefinition: DemoRuleLogicDefinition | null;
    testIds?: typeof INTEGRATED_VALIDATION_TEST_IDS;
    onOutcomeRoutingChange?: (routingId: string, patch: Partial<RuleOutcomeRouting>) => void;
    onSimplifiedLogicEdit?: (edit: RuleSimplifiedLogicEdit) => void;
};

const IntegratedValidationPanel = ({
    workbenchView,
    draftRuleDefinition,
    testIds,
    onOutcomeRoutingChange,
    onSimplifiedLogicEdit,
}: IntegratedValidationPanelProps) => {
    const summaryPresentation = workbenchView.simplifiedLogicPresentation;

    return (
        <DrawerSection
            data-testid={testIds?.section}
            data-appglue-executable-type={workbenchView.editorHostKey ?? undefined}
        >
            <DrawerSectionTitle>Validation Preview</DrawerSectionTitle>
            <PreviewGrid>
                <MetricCard>
                    <MetricLabel>Pass</MetricLabel>
                    <MetricValue data-testid={testIds?.pass}>
                        {formatInteger(workbenchView.draftRuleCounts?.pass ?? workbenchView.currentRuleCounts?.pass ?? 0)}
                    </MetricValue>
                    <MetricDelta>was {formatInteger(workbenchView.currentRuleCounts?.pass ?? 0)}</MetricDelta>
                </MetricCard>
                <MetricCard>
                    <MetricLabel>Flagged</MetricLabel>
                    <MetricValue
                        $tone="warning"
                        data-testid={testIds?.flagged}
                    >
                        {formatInteger(
                            workbenchView.draftRuleCounts?.flagged ?? workbenchView.currentRuleCounts?.flagged ?? 0,
                        )}
                    </MetricValue>
                    <MetricDelta>was {formatInteger(workbenchView.currentRuleCounts?.flagged ?? 0)}</MetricDelta>
                </MetricCard>
                <MetricCard>
                    <MetricLabel>Rejected</MetricLabel>
                    <MetricValue
                        $tone="danger"
                        data-testid={testIds?.rejected}
                    >
                        {formatInteger(
                            workbenchView.draftRuleCounts?.rejected ?? workbenchView.currentRuleCounts?.rejected ?? 0,
                        )}
                    </MetricValue>
                    <MetricDelta>was {formatInteger(workbenchView.currentRuleCounts?.rejected ?? 0)}</MetricDelta>
                </MetricCard>
            </PreviewGrid>
            <LogicBox>
                <LogicTitle>Generated simplified logic</LogicTitle>
                {summaryPresentation.status !== "complete" && (
                    <LogicWarning>
                        This native AppGlue expression is shown read-only because it cannot be safely reverse-edited
                        from the simplified summary. Use the native editor for full changes.
                        {summaryPresentation.warnings[0] ? ` ${summaryPresentation.warnings[0]}` : ""}
                    </LogicWarning>
                )}
                {workbenchView.simplifiedLogicEditorModel ? (
                    <EditableSimplifiedLogicList
                        model={workbenchView.simplifiedLogicEditorModel}
                        staticLines={workbenchView.simplifiedLogicLines.slice(
                            workbenchView.simplifiedLogicEditorModel.requirement ? 2 : 1,
                        )}
                        onEdit={onSimplifiedLogicEdit}
                    />
                ) : (
                    <LogicList>
                        {workbenchView.simplifiedLogicLines.map((line) => (
                            <li key={line}>
                                <code>{line}</code>
                            </li>
                        ))}
                    </LogicList>
                )}
            </LogicBox>
            {draftRuleDefinition?.outcomeRouting?.length ? (
                <ActionRoutingEditor
                    routing={draftRuleDefinition.outcomeRouting}
                    onChange={onOutcomeRoutingChange}
                />
            ) : null}
        </DrawerSection>
    );
};

type EditableSimplifiedLogicListProps = {
    model: NonNullable<IntegratedRuleWorkbenchViewModel["simplifiedLogicEditorModel"]>;
    staticLines: string[];
    onEdit?: (edit: RuleSimplifiedLogicEdit) => void;
};

const parseEditableValueList = (value: string): string[] =>
    value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

const normalizeEditableOptionValues = (
    value: string,
    options: { value: string; label: string }[],
): string[] => {
    if (options.length === 0) return parseEditableValueList(value);

    const optionValuesByLowercase = new Map(options.map((option) => [option.value.toLowerCase(), option.value]));
    const optionLabelsByLowercase = new Map(options.map((option) => [option.label.toLowerCase(), option.value]));

    return parseEditableValueList(value)
        .map((part) => {
            const key = part.toLowerCase();
            return optionValuesByLowercase.get(key) ?? optionLabelsByLowercase.get(key);
        })
        .filter((part): part is string => !!part);
};

const EditableSimplifiedLogicList = ({ model, staticLines, onEdit }: EditableSimplifiedLogicListProps) => {
    const { condition } = model;
    const conditionValueText = condition.values.join(", ");
    const requirementValueText = model.requirement?.values.join(", ") ?? "";
    const [draftValueText, setDraftValueText] = useState(conditionValueText);
    const [draftRequirementText, setDraftRequirementText] = useState(requirementValueText);

    useEffect(() => {
        setDraftValueText(conditionValueText);
    }, [condition.id, conditionValueText]);

    useEffect(() => {
        setDraftRequirementText(requirementValueText);
    }, [model.requirement?.id, requirementValueText]);

    const commitValues = () => {
        const values = normalizeEditableOptionValues(draftValueText, condition.valueOptions);
        if (values.length === 0) {
            setDraftValueText(conditionValueText);
            return;
        }
        onEdit?.({
            conditionId: condition.id,
            values,
        });
    };

    const commitRequirementValues = () => {
        if (!model.requirement) return;

        const values = normalizeEditableOptionValues(draftRequirementText, model.requirement.valueOptions);
        if (values.length === 0) {
            setDraftRequirementText(requirementValueText);
            return;
        }
        onEdit?.({
            conditionId: condition.id,
            requirementId: model.requirement.id,
            requirementValues: values,
        });
    };

    return (
        <EditableLogicList>
            <EditableLogicLine>
                <LogicKeyword>{condition.keyword}</LogicKeyword>
                <LogicSegmentSelect
                    aria-label={`${condition.id} field`}
                    value={condition.field}
                    disabled={!onEdit}
                    onChange={(event) =>
                        onEdit?.({
                            conditionId: condition.id,
                            field: event.target.value,
                        })
                    }
                >
                    {condition.fieldOptions.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                </LogicSegmentSelect>
                <LogicOperator>{condition.operatorLabel}</LogicOperator>
                <LogicSegmentInput
                    aria-label={condition.valueInputLabel}
                    value={draftValueText}
                    disabled={!onEdit}
                    list={`${condition.id}-values`}
                    onChange={(event) => setDraftValueText(event.target.value)}
                    onBlur={commitValues}
                    onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        commitValues();
                    }}
                />
                <datalist id={`${condition.id}-values`}>
                    {condition.valueOptions.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                </datalist>
            </EditableLogicLine>
            {model.requirement ? (
                <EditableLogicLine>
                    <LogicKeyword>{model.requirement.keyword}</LogicKeyword>
                    <LogicSegmentInput
                        aria-label={model.requirement.valueInputLabel}
                        value={draftRequirementText}
                        disabled={!onEdit}
                        list={`${model.requirement.id}-values`}
                        onChange={(event) => setDraftRequirementText(event.target.value)}
                        onBlur={commitRequirementValues}
                        onKeyDown={(event) => {
                            if (event.key !== "Enter") return;
                            event.preventDefault();
                            commitRequirementValues();
                        }}
                    />
                    <datalist id={`${model.requirement.id}-values`}>
                        {model.requirement.valueOptions.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </option>
                        ))}
                    </datalist>
                </EditableLogicLine>
            ) : null}
            {staticLines.map((line) => (
                <li key={line}>
                    <code>{line}</code>
                </li>
            ))}
        </EditableLogicList>
    );
};

type ActionRoutingEditorProps = {
    routing: RuleOutcomeRouting[];
    onChange?: (routingId: string, patch: Partial<RuleOutcomeRouting>) => void;
};

const ActionRoutingEditor = ({ routing, onChange }: ActionRoutingEditorProps) => (
    <ActionRoutingBox data-testid="electronics-action-routing-editor">
        <LogicTitle>Action routing</LogicTitle>
        <ActionRoutingRows>
            {routing.map((route) => (
                <ActionRoutingRow
                    key={route.id}
                    data-testid="electronics-action-routing-row"
                >
                    <RouteWhen>{route.when}</RouteWhen>
                    <ActionRoutingField>
                        <ActionRoutingLabel>Status</ActionRoutingLabel>
                        <ActionRoutingSelect
                            aria-label={`${route.when} outcome status`}
                            value={route.status}
                            disabled={!onChange}
                            onChange={(event) =>
                                onChange?.(route.id, {
                                    status: event.target.value as Exclude<DemoRuleStatus, "skipped">,
                                })
                            }
                        >
                            <option value="pass">pass</option>
                            <option value="flagged">flagged</option>
                            <option value="rejected">rejected</option>
                        </ActionRoutingSelect>
                    </ActionRoutingField>
                    <ActionRoutingField>
                        <ActionRoutingLabel>Action</ActionRoutingLabel>
                        <ActionRoutingInput
                            aria-label={`${route.when} action label`}
                            value={route.label}
                            disabled={!onChange}
                            onChange={(event) => onChange?.(route.id, { label: event.target.value })}
                        />
                    </ActionRoutingField>
                    <ActionRoutingField>
                        <ActionRoutingLabel>Queue</ActionRoutingLabel>
                        <ActionRoutingSelect
                            aria-label={`${route.when} output queue`}
                            value={route.outputQueue ?? ""}
                            disabled={!onChange}
                            onChange={(event) =>
                                onChange?.(route.id, {
                                    outputQueue: (event.target.value || undefined) as DemoOutputQueue | undefined,
                                })
                            }
                        >
                            <option value="">none</option>
                            {OUTPUT_QUEUE_OPTIONS.map((queue) => (
                                <option
                                    key={queue}
                                    value={queue}
                                >
                                    {queue}
                                </option>
                            ))}
                        </ActionRoutingSelect>
                    </ActionRoutingField>
                    <ActionRoutingField>
                        <ActionRoutingLabel>Reason</ActionRoutingLabel>
                        <ActionRoutingInput
                            aria-label={`${route.when} reason code`}
                            value={route.reasonCode}
                            disabled={!onChange}
                            onChange={(event) => onChange?.(route.id, { reasonCode: event.target.value })}
                        />
                    </ActionRoutingField>
                    <ActionRoutingField $span={2}>
                        <ActionRoutingLabel>Explanation</ActionRoutingLabel>
                        <ActionRoutingInput
                            aria-label={`${route.when} explanation`}
                            value={route.explanation}
                            disabled={!onChange}
                            onChange={(event) => onChange?.(route.id, { explanation: event.target.value })}
                        />
                    </ActionRoutingField>
                </ActionRoutingRow>
            ))}
        </ActionRoutingRows>
    </ActionRoutingBox>
);

const parseConditionValue = (operator: RuleLogicOperator, value: string): string | number | string[] | undefined => {
    if (operator === "exists") return undefined;
    if (operator === "in" || operator === "not_in") {
        return value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }
    if ([">", ">=", "<", "<="].includes(operator)) {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value;
    }
    return value;
};

const conditionValueText = (condition: RuleLogicCondition): string => {
    if (Array.isArray(condition.value)) return condition.value.join(", ");
    return condition.value === undefined ? "" : String(condition.value);
};

const requirementsText = (definition: DemoRuleLogicDefinition): string =>
    definition.requirements.flatMap((requirement) => requirement.fields).join(", ");

const actionText = (definition: DemoRuleLogicDefinition): string =>
    definition.actions.map((action) => action.label).join("\n");

const integrationConfigSourceText = (source: string): string => {
    if (source === "saved_storage") return "saved AppGlue storage";
    if (source === "saved_storage_fallback") return "saved storage fallback";
    return "seeded AppGlue factory";
};

export const ElectronicsDataDecisioningDemo = () => {
    const baseDemoData = useMemo(() => generateDemoData(), []);
    const [priceToleranceRules, setPriceToleranceRules] = useState<PriceToleranceRule[]>(
        () => baseDemoData.priceToleranceRules,
    );
    const [taxonomyMappings, setTaxonomyMappings] = useState<TaxonomyProductMapping[]>(
        () => baseDemoData.taxonomyMappings,
    );
    const demoData = useMemo(
        () => ({ ...baseDemoData, priceToleranceRules, taxonomyMappings }),
        [baseDemoData, priceToleranceRules, taxonomyMappings],
    );
    const [workbenchState, setWorkbenchState] = useState(() =>
        createInitialIntegratedRuleWorkbenchState(browserWorkbenchEnvironment()),
    );
    const rules = useMemo(
        () =>
            workbenchState.ruleOrder
                .map((ruleId) => workbenchState.ruleMetadataById[ruleId])
                .filter((rule): rule is DemoRule => !!rule),
        [workbenchState.ruleMetadataById, workbenchState.ruleOrder],
    );
    const ruleDefinitionsById = workbenchState.ruleDefinitionsById;
    const editingRuleId = workbenchState.editingRuleId;
    const draftRuleDefinition = workbenchState.draftRuleDefinition;
    const draftIntegratedRuleConfig = workbenchState.draftIntegratedRuleConfig;
    const askSuggestion = workbenchState.askSuggestion;
    const eventEvaluation = useMemo(
        () => buildDecisionEventEvaluation(demoData, { maxEvents: 10000, ruleDefinitionsById, rules }),
        [demoData, ruleDefinitionsById, rules],
    );
    const eventContainerRef = useRef<HTMLDivElement | null>(null);
    const [selectedRuleId, setSelectedRuleId] = useState("DQ-001");
    const [expandedRuleId, setExpandedRuleId] = useState<string | null>("DQ-001");
    const [ruleFilter, setRuleFilter] = useState("DQ-001");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>("all");
    const [search, setSearch] = useState("");
    const [scrollTop, setScrollTop] = useState(0);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [askPrompt, setAskPrompt] = useState("");
    const selectedRule = rules.find((rule) => rule.id === selectedRuleId) ?? rules[0];
    const editingRule = editingRuleId ? rules.find((rule) => rule.id === editingRuleId) : null;

    useEffect(() => {
        saveRuleCatalog(
            {
                ruleOrder: workbenchState.ruleOrder,
                ruleMetadataById: workbenchState.ruleMetadataById,
                ruleDefinitionsById,
            },
            browserWorkbenchEnvironment(),
        );
    }, [ruleDefinitionsById, workbenchState.ruleMetadataById, workbenchState.ruleOrder]);

    const resetScroll = () => {
        setScrollTop(0);
        if (eventContainerRef.current) eventContainerRef.current.scrollTop = 0;
    };

    const toggleRuleExpansion = (ruleId: string) => {
        setSelectedRuleId(ruleId);
        setExpandedRuleId((currentRuleId) => (currentRuleId === ruleId ? null : ruleId));
        setRuleFilter(ruleId);
        setSelectedEventId(null);
        resetScroll();
    };

    const showAllEvents = () => {
        setRuleFilter("all");
        setStatusFilter("all");
        setEventTypeFilter("all");
        setSearch("");
        setSelectedEventId(null);
        resetScroll();
    };

    const openRuleEditor = (ruleId: string) => {
        const rule = rules.find((candidate) => candidate.id === ruleId);
        setSelectedEventId(null);
        setWorkbenchState((current) =>
            openIntegratedRuleEditor(current, {
                ruleId,
                priceToleranceRules,
                taxonomyMappings,
            }),
        );
        setAskPrompt(rule?.examplePrompt ?? "");
    };

    const createRuleFromSelection = () => {
        if (!selectedRule) return;

        const result = createRule(workbenchState, { templateRuleId: selectedRule.id, afterRuleId: selectedRule.id });
        setWorkbenchState(result.state);
        setSelectedRuleId(result.createdRuleId);
        setExpandedRuleId(result.createdRuleId);
        setRuleFilter(result.createdRuleId);
        setSelectedEventId(null);
        resetScroll();
    };

    const duplicateSelectedRule = (ruleId: string) => {
        const result = duplicateRule(workbenchState, ruleId);
        setWorkbenchState(result.state);
        setSelectedRuleId(result.createdRuleId);
        setExpandedRuleId(result.createdRuleId);
        setRuleFilter(result.createdRuleId);
        setSelectedEventId(null);
        resetScroll();
    };

    const deleteSelectedRule = (ruleId: string) => {
        const result = deleteRule(workbenchState, ruleId);
        setWorkbenchState(result.state);
        if (!result.deletedRuleId || !result.replacementRuleId) return;

        setSelectedRuleId(result.replacementRuleId);
        setExpandedRuleId(result.replacementRuleId);
        setRuleFilter((currentRuleFilter) => (currentRuleFilter === ruleId ? result.replacementRuleId! : currentRuleFilter));
        if (result.shouldClearSelectedEvent) setSelectedEventId(null);
        resetScroll();
    };

    const moveRule = (ruleId: string, direction: "up" | "down") => {
        setWorkbenchState((current) => reorderRules(current, ruleId, direction));
    };

    const closeRuleEditor = () => {
        setWorkbenchState(closeIntegratedRuleEditor);
    };

    const updateDraft = (updater: (definition: DemoRuleLogicDefinition) => DemoRuleLogicDefinition) => {
        setWorkbenchState((current) => updateIntegratedRuleDraftDefinition(current, updater));
    };

    const updateOutcomeRouting = (routingId: string, patch: Partial<RuleOutcomeRouting>) => {
        setWorkbenchState((current) => updateIntegratedRuleOutcomeRouting(current, routingId, patch));
    };

    const updateSimplifiedLogic = (edit: RuleSimplifiedLogicEdit) => {
        setWorkbenchState((current) => applyIntegratedRuleSimplifiedLogicEdit(current, edit));
    };

    const saveDraftRule = () => {
        const result = saveIntegratedRuleDraft(workbenchState);
        if (!result.savedRuleId) return;

        setWorkbenchState(result.state);
        if (result.projectedPriceToleranceRules) setPriceToleranceRules(result.projectedPriceToleranceRules);
        if (result.projectedTaxonomyMappings) setTaxonomyMappings(result.projectedTaxonomyMappings);
        setSelectedRuleId(result.savedRuleId);
        setExpandedRuleId(result.savedRuleId);
        setRuleFilter(result.savedRuleId);
        setSelectedEventId(null);
        resetScroll();
    };

    const resetDraftRule = () => {
        setWorkbenchState((current) =>
            resetIntegratedRuleDraft(current, {
                basePriceToleranceRules: baseDemoData.priceToleranceRules,
                baseTaxonomyMappings: baseDemoData.taxonomyMappings,
            }),
        );
    };

    const resetAllRuleDefinitions = () => {
        setWorkbenchState(resetIntegratedRuleWorkbench());
        setPriceToleranceRules(baseDemoData.priceToleranceRules);
        setTaxonomyMappings(baseDemoData.taxonomyMappings);
        setSelectedRuleId("DQ-001");
        setExpandedRuleId("DQ-001");
        setRuleFilter("DQ-001");
        setSelectedEventId(null);
        resetScroll();
    };

    const applyAskSuggestion = () => {
        setWorkbenchState((current) =>
            applyIntegratedRuleSuggestion(current, {
                provider: stubRuleSuggestionProvider,
                prompt: askPrompt,
                data: demoData,
            }),
        );
    };

    const draftRuleDefinitionForEvaluation = useMemo(() => {
        return getDraftRuleDefinitionForEvaluation(workbenchState);
    }, [workbenchState]);

    const filteredEvents = useMemo(
        () =>
            eventEvaluation.events.filter((event) => {
                if (ruleFilter !== "all" && !selectedRuleOutcome(event, ruleFilter)) return false;
                if (statusFilter !== "all" && event.overallStatus !== statusFilter) return false;
                if (eventTypeFilter !== "all" && event.eventType !== eventTypeFilter) return false;
                return eventMatchesSearch(event, search);
            }),
        [eventEvaluation.events, eventTypeFilter, ruleFilter, search, statusFilter],
    );

    const visibleRowCount = Math.ceil(EVENT_VIEWPORT_HEIGHT / ROW_HEIGHT) + OVERSCAN_ROWS * 2;
    const rawStartIndex = Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS;
    const startIndex = Math.min(Math.max(0, filteredEvents.length - visibleRowCount), Math.max(0, rawStartIndex));
    const visibleEvents = filteredEvents.slice(startIndex, startIndex + visibleRowCount);
    const selectedEvent = selectedEventId
        ? eventEvaluation.events.find((event) => event.eventId === selectedEventId)
        : null;
    const draftEvaluation = useMemo(() => {
        if (!editingRuleId || !draftRuleDefinitionForEvaluation) return null;
        return buildDecisionEventEvaluation(demoData, {
            maxEvents: 10000,
            ruleDefinitionsById: {
                ...ruleDefinitionsById,
                [editingRuleId]: draftRuleDefinitionForEvaluation,
            },
            rules,
        });
    }, [demoData, draftRuleDefinitionForEvaluation, editingRuleId, ruleDefinitionsById, rules]);
    const currentRuleCounts = editingRuleId ? eventEvaluation.summary.byRule[editingRuleId] : undefined;
    const draftRuleCounts = editingRuleId ? draftEvaluation?.summary.byRule[editingRuleId] : undefined;
    const workbenchView = useMemo(
        () =>
            createIntegratedRuleWorkbenchViewModel(workbenchState, {
                currentPriceToleranceRules: priceToleranceRules,
                currentTaxonomyMappings: taxonomyMappings,
                currentRuleCounts,
                draftRuleCounts,
            }),
        [currentRuleCounts, draftRuleCounts, priceToleranceRules, taxonomyMappings, workbenchState],
    );
    const isEditingIntegratedRule = workbenchView.isEditingIntegratedRule;
    const isIntegratedEditorWide = isWideIntegratedRuleEditor(draftIntegratedRuleConfig);

    const refreshDraftIntegratedRuleStorage = () => {
        setWorkbenchState(refreshIntegratedRuleDraftStorage);
    };

    return (
        <DemoSurface>
            <DemoHeader>
                <HeaderIdentity>
                    <HeaderIcon>AG</HeaderIcon>
                    <div>
                        <Title>Data Decisioning Rules</Title>
                        <Subtitle>View rules | Evaluate supplier events | Explain outcomes</Subtitle>
                    </div>
                </HeaderIdentity>
                <HeaderActions>
                    <SecondaryButton
                        type="button"
                        onClick={resetAllRuleDefinitions}
                    >
                        Reset rules
                    </SecondaryButton>
                    <HeaderBadge>{formatInteger(eventEvaluation.summary.totalEvents)} events</HeaderBadge>
                    <HeaderBadge>{formatInteger(rules.length)} rules</HeaderBadge>
                    <HeaderBadge>Demo</HeaderBadge>
                </HeaderActions>
            </DemoHeader>

            <DemoGrid>
                <LeftColumn>
                    <Panel>
                        <PanelHeader>
                            <PanelTitle>Decision Rules</PanelTitle>
                        </PanelHeader>
                        <RulesetControl>
                            <RulesetLabel>Ruleset</RulesetLabel>
                            <RulesetRow>
                                <RulesetValue>ELECTRONICS-DATA-DECISIONING-V1</RulesetValue>
                                <SecondaryButton
                                    type="button"
                                    onClick={showAllEvents}
                                >
                                    Show all
                                </SecondaryButton>
                            </RulesetRow>
                            <RulesetNote>
                                Representative electronics distributor dataset -{" "}
                                {formatInteger(demoData.summary.partRows)} part rows,{" "}
                                {formatInteger(demoData.summary.priceUpdateRows)} price updates.
                            </RulesetNote>
                        </RulesetControl>
                    </Panel>

                    <Panel>
                        <PanelHeaderRow>
                            <PanelTitle>Rules</PanelTitle>
                            <RuleToolbar>
                                <SecondaryButton
                                    type="button"
                                    aria-label="Add rule from selected"
                                    disabled={!selectedRule}
                                    onClick={createRuleFromSelection}
                                >
                                    Add rule
                                </SecondaryButton>
                                <SecondaryButton
                                    type="button"
                                    disabled={!selectedRule}
                                    onClick={() => {
                                        if (!selectedRule) return;
                                        setRuleFilter(selectedRule.id);
                                        resetScroll();
                                    }}
                                >
                                    Filter events
                                </SecondaryButton>
                            </RuleToolbar>
                        </PanelHeaderRow>
                        <RuleList>
                            {rules.map((rule, ruleIndex) => {
                                const ruleDefinition = getRuleLogicDefinition(ruleDefinitionsById, rule.id);
                                const counts = eventEvaluation.summary.byRule[rule.id] ?? {
                                    pass: 0,
                                    flagged: 0,
                                    rejected: 0,
                                    skipped: 0,
                                    errored: 0,
                                };
                                const routedCount = counts.flagged + counts.rejected + counts.errored;
                                const isExpanded = expandedRuleId === rule.id;
                                return (
                                    <RuleItem
                                        key={rule.id}
                                        data-testid="electronics-rule-card"
                                        data-rule-id={rule.id}
                                        $selected={isExpanded}
                                    >
                                        <RuleButton
                                            type="button"
                                            onClick={() => toggleRuleExpansion(rule.id)}
                                        >
                                            <RuleTopline>
                                                <RuleId>{rule.id}</RuleId>
                                                <RuleBadges>
                                                    {counts.rejected > 0 && (
                                                        <StatusBadge $tone="danger">
                                                            {formatInteger(counts.rejected)} reject
                                                        </StatusBadge>
                                                    )}
                                                    {counts.flagged > 0 && (
                                                        <StatusBadge $tone="warning">
                                                            {formatInteger(counts.flagged)} flag
                                                        </StatusBadge>
                                                    )}
                                                    {routedCount === 0 && (
                                                        <StatusBadge $tone="success">
                                                            {formatInteger(counts.pass)} pass
                                                        </StatusBadge>
                                                    )}
                                                    {isRuleModified(ruleDefinition) && (
                                                        <StatusBadge $tone="neutral">modified</StatusBadge>
                                                    )}
                                                    <StatusBadge $tone="neutral">
                                                        {describeRuleIntegrationStatus(
                                                            ruleDefinition.integrationStatus,
                                                        )}
                                                    </StatusBadge>
                                                    <CategoryBadge>{rule.category}</CategoryBadge>
                                                </RuleBadges>
                                            </RuleTopline>
                                            <RuleName>{rule.name}</RuleName>
                                            <RuleMeta>
                                                {rule.scope} | {ruleDefinition.trigger}
                                            </RuleMeta>
                                        </RuleButton>

                                        {isExpanded && (
                                            <RuleExpansion>
                                                <RuleDescription>{rule.capability}</RuleDescription>
                                                <TriggerLine>
                                                    Trigger: <code>{ruleDefinition.trigger}</code>
                                                </TriggerLine>
                                                <TriggerLine>
                                                    Integration:{" "}
                                                    <code>
                                                        {describeRuleIntegrationStatus(
                                                            ruleDefinition.integrationStatus,
                                                        )}
                                                    </code>
                                                </TriggerLine>
                                                <TriggerLine>
                                                    Target AppGlue object:{" "}
                                                    <code>{ruleDefinition.targetAppGlueObjectLabel}</code>
                                                </TriggerLine>
                                                <LogicBox>
                                                    <LogicTitle>Logic</LogicTitle>
                                                    <LogicList>
                                                        {renderDisplayRuleLogicLines(
                                                            ruleDefinition,
                                                            undefined,
                                                            priceToleranceRules,
                                                            taxonomyMappings,
                                                        ).map((line) => (
                                                            <li key={line}>
                                                                <code>{line}</code>
                                                            </li>
                                                        ))}
                                                    </LogicList>
                                                </LogicBox>
                                                <RuleActions>
                                                    <SecondaryButton
                                                        type="button"
                                                        onClick={() => {
                                                            setRuleFilter(rule.id);
                                                            resetScroll();
                                                        }}
                                                    >
                                                        Filter events
                                                    </SecondaryButton>
                                                    <GhostButton
                                                        type="button"
                                                        onClick={() => openRuleEditor(rule.id)}
                                                    >
                                                        Edit rule
                                                    </GhostButton>
                                                    <GhostButton
                                                        type="button"
                                                        aria-label={`Duplicate ${rule.id}`}
                                                        onClick={() => duplicateSelectedRule(rule.id)}
                                                    >
                                                        Duplicate
                                                    </GhostButton>
                                                    <GhostButton
                                                        type="button"
                                                        aria-label={`Move ${rule.id} up`}
                                                        disabled={ruleIndex === 0}
                                                        onClick={() => moveRule(rule.id, "up")}
                                                    >
                                                        Up
                                                    </GhostButton>
                                                    <GhostButton
                                                        type="button"
                                                        aria-label={`Move ${rule.id} down`}
                                                        disabled={ruleIndex === rules.length - 1}
                                                        onClick={() => moveRule(rule.id, "down")}
                                                    >
                                                        Down
                                                    </GhostButton>
                                                    <GhostButton
                                                        type="button"
                                                        aria-label={`Delete ${rule.id}`}
                                                        disabled={rules.length <= 1}
                                                        onClick={() => deleteSelectedRule(rule.id)}
                                                    >
                                                        Delete
                                                    </GhostButton>
                                                </RuleActions>
                                            </RuleExpansion>
                                        )}
                                    </RuleItem>
                                );
                            })}
                        </RuleList>
                    </Panel>
                </LeftColumn>

                <RightColumn>
                    <Panel>
                        <PanelHeaderRow>
                            <div>
                                <PanelTitle>Evaluation</PanelTitle>
                                <PanelNote>
                                    {formatInteger(filteredEvents.length)} /{" "}
                                    {formatInteger(eventEvaluation.events.length)} events shown
                                </PanelNote>
                            </div>
                            {ruleFilter !== "all" && <FilterChip>Rule: {ruleFilter}</FilterChip>}
                        </PanelHeaderRow>
                        <EvaluationGrid>
                            <MetricCard>
                                <MetricLabel>Pass</MetricLabel>
                                <MetricValue>{formatInteger(eventEvaluation.summary.pass)}</MetricValue>
                            </MetricCard>
                            <MetricCard>
                                <MetricLabel>Flagged</MetricLabel>
                                <MetricValue $tone="warning">
                                    {formatInteger(eventEvaluation.summary.flagged)}
                                </MetricValue>
                            </MetricCard>
                            <MetricCard>
                                <MetricLabel>Rejected</MetricLabel>
                                <MetricValue $tone="danger">
                                    {formatInteger(eventEvaluation.summary.rejected)}
                                </MetricValue>
                            </MetricCard>
                            <MetricCard>
                                <MetricLabel>Errors</MetricLabel>
                                <MetricValue $tone="danger">
                                    {formatInteger(eventEvaluation.summary.errored)}
                                </MetricValue>
                            </MetricCard>
                        </EvaluationGrid>
                        <FilterGrid>
                            <FilterField>
                                <FilterLabel htmlFor="status-filter">Status</FilterLabel>
                                <Select
                                    id="status-filter"
                                    value={statusFilter}
                                    onChange={(event) => {
                                        setStatusFilter(event.target.value as StatusFilter);
                                        resetScroll();
                                    }}
                                >
                                    <option value="all">All</option>
                                    <option value="pass">Pass</option>
                                    <option value="flagged">Flag</option>
                                    <option value="rejected">Reject</option>
                                    <option value="error">Error</option>
                                </Select>
                            </FilterField>
                            <FilterField>
                                <FilterLabel htmlFor="event-type-filter">Event type</FilterLabel>
                                <Select
                                    id="event-type-filter"
                                    value={eventTypeFilter}
                                    onChange={(event) => {
                                        setEventTypeFilter(event.target.value as EventTypeFilter);
                                        resetScroll();
                                    }}
                                >
                                    <option value="all">All</option>
                                    {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                                        <option
                                            key={value}
                                            value={value}
                                        >
                                            {label}
                                        </option>
                                    ))}
                                </Select>
                            </FilterField>
                            <FilterField>
                                <FilterLabel htmlFor="rule-filter">Rule</FilterLabel>
                                <Select
                                    id="rule-filter"
                                    value={ruleFilter}
                                    onChange={(event) => {
                                        setRuleFilter(event.target.value);
                                        resetScroll();
                                    }}
                                >
                                    <option value="all">All</option>
                                    {rules.map((rule) => (
                                        <option
                                            key={rule.id}
                                            value={rule.id}
                                        >
                                            {rule.id}
                                        </option>
                                    ))}
                                </Select>
                            </FilterField>
                            <FilterField>
                                <FilterLabel htmlFor="event-search">Search</FilterLabel>
                                <Input
                                    id="event-search"
                                    value={search}
                                    onChange={(event) => {
                                        setSearch(event.target.value);
                                        resetScroll();
                                    }}
                                    placeholder="id, MPN, reason..."
                                />
                            </FilterField>
                        </FilterGrid>
                    </Panel>

                    <Panel>
                        <PanelHeaderRow>
                            <PanelTitle>Events</PanelTitle>
                            {selectedEvent && <PanelNote>Selected: {selectedEvent.eventId}</PanelNote>}
                        </PanelHeaderRow>
                        <EventTable>
                            <EventHeader>
                                <span>Event</span>
                                <span>Subject</span>
                                <span>Headline</span>
                                <span>Rule</span>
                                <span>Status</span>
                            </EventHeader>
                            <EventViewport
                                ref={eventContainerRef}
                                onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
                            >
                                <EventRows
                                    style={{
                                        height: Math.max(filteredEvents.length * ROW_HEIGHT, EVENT_VIEWPORT_HEIGHT),
                                    }}
                                >
                                    {visibleEvents.map((event, offset) => {
                                        const absoluteIndex = startIndex + offset;
                                        const selectedOutcome =
                                            ruleFilter === "all" ? undefined : selectedRuleOutcome(event, ruleFilter);
                                        const displayRuleId = selectedOutcome?.ruleId ?? event.primaryRuleId ?? "-";
                                        return (
                                            <EventRow
                                                key={event.eventId}
                                                type="button"
                                                $tone={statusTone(event.overallStatus)}
                                                $selected={selectedEventId === event.eventId}
                                                style={{ transform: `translateY(${absoluteIndex * ROW_HEIGHT}px)` }}
                                                onClick={() => setSelectedEventId(event.eventId)}
                                            >
                                                <EventCell>
                                                    <EventType>{EVENT_TYPE_LABELS[event.eventType]}</EventType>
                                                    <MutedMono>{event.eventId}</MutedMono>
                                                </EventCell>
                                                <EventCell>
                                                    <StrongText>{event.subjectLabel}</StrongText>
                                                    <MutedMono>{event.subjectId}</MutedMono>
                                                </EventCell>
                                                <HeadlineCell>
                                                    {selectedOutcome?.explanation ?? event.headline}
                                                </HeadlineCell>
                                                <EventCell>
                                                    <RulePill>{displayRuleId}</RulePill>
                                                </EventCell>
                                                <EventCell>
                                                    <StatusBadge $tone={statusTone(event.overallStatus)}>
                                                        {statusText(event.overallStatus)}
                                                    </StatusBadge>
                                                </EventCell>
                                            </EventRow>
                                        );
                                    })}
                                    {filteredEvents.length === 0 && (
                                        <EmptyState>No events match the current filters.</EmptyState>
                                    )}
                                </EventRows>
                            </EventViewport>
                        </EventTable>
                    </Panel>
                </RightColumn>
            </DemoGrid>

            {selectedEvent && (
                <DrawerLayer>
                    <DrawerBackdrop onClick={() => setSelectedEventId(null)} />
                    <DrawerPanel>
                        <DrawerHeader>
                            <div>
                                <DrawerTitle>{selectedEvent.eventId}</DrawerTitle>
                                <DrawerSubtitle>
                                    {EVENT_TYPE_LABELS[selectedEvent.eventType]} | {selectedEvent.subjectLabel}
                                </DrawerSubtitle>
                            </div>
                            <CloseButton
                                type="button"
                                aria-label="Close event detail"
                                onClick={() => setSelectedEventId(null)}
                            >
                                x
                            </CloseButton>
                        </DrawerHeader>

                        <DrawerBody>
                            <DetailSummary>
                                <SummaryRow>
                                    <SummaryLabel>Overall</SummaryLabel>
                                    <StatusBadge $tone={statusTone(selectedEvent.overallStatus)}>
                                        {statusText(selectedEvent.overallStatus)}
                                    </StatusBadge>
                                </SummaryRow>
                                <SummaryRow>
                                    <SummaryLabel>Primary rule</SummaryLabel>
                                    <RulePill>{selectedEvent.primaryRuleId ?? "-"}</RulePill>
                                </SummaryRow>
                                <SummaryRow>
                                    <SummaryLabel>Source table</SummaryLabel>
                                    <MonoValue>{selectedEvent.sourceTable}</MonoValue>
                                </SummaryRow>
                                <SummaryRow>
                                    <SummaryLabel>Downstream action</SummaryLabel>
                                    <SummaryValue>{selectedEvent.downstreamAction}</SummaryValue>
                                </SummaryRow>
                                <SummaryRow>
                                    <SummaryLabel>Output queue</SummaryLabel>
                                    <MonoValue>{selectedEvent.outputQueue ?? "none"}</MonoValue>
                                </SummaryRow>
                            </DetailSummary>

                            <DrawerSection>
                                <DrawerSectionTitle>Downstream Action</DrawerSectionTitle>
                                <ActionBox>
                                    <SummaryValue>{selectedEvent.downstreamAction}</SummaryValue>
                                    <OutcomeMeta>
                                        Output queue: <code>{selectedEvent.outputQueue ?? "none"}</code>
                                    </OutcomeMeta>
                                </ActionBox>
                            </DrawerSection>

                            <DrawerSection>
                                <DrawerSectionTitle>Source Event Summary</DrawerSectionTitle>
                                <ContextTable>
                                    {Object.entries(selectedEvent.contextPreview).map(([key, value]) => (
                                        <ContextRow key={key}>
                                            <ContextKey>{contextLabel(key)}</ContextKey>
                                            <ContextValue>{String(value ?? "-")}</ContextValue>
                                        </ContextRow>
                                    ))}
                                </ContextTable>
                            </DrawerSection>

                            <DrawerSection>
                                <DrawerSectionTitle>Rule Outcomes</DrawerSectionTitle>
                                <OutcomeList>
                                    {selectedEvent.ruleOutcomes.map((outcome) => (
                                        <OutcomeCard key={outcome.ruleId}>
                                            <OutcomeHeader>
                                                <div>
                                                    <OutcomeRule>{outcome.ruleId}</OutcomeRule>
                                                    <OutcomeName>{outcome.ruleName}</OutcomeName>
                                                </div>
                                                <StatusBadge $tone={statusTone(outcome.status)}>
                                                    {statusText(outcome.status)}
                                                </StatusBadge>
                                            </OutcomeHeader>
                                            <OutcomeText>{outcome.explanation}</OutcomeText>
                                            <OutcomeMeta>
                                                Reason: <code>{outcome.reasonCode}</code>
                                                {outcome.outputQueue ? (
                                                    <>
                                                        {" "}
                                                        | Output: <code>{outcome.outputQueue}</code>
                                                    </>
                                                ) : null}
                                            </OutcomeMeta>
                                            {outcome.integrationEvidence && (
                                                <OutcomeMeta>
                                                    AppGlue: <code>{outcome.integrationEvidence.executableType}</code>
                                                    {" | "}Config:{" "}
                                                    <code>
                                                        {integrationConfigSourceText(
                                                            outcome.integrationEvidence.configSource,
                                                        )}
                                                    </code>
                                                </OutcomeMeta>
                                            )}
                                            {outcome.comparisons.length > 0 && (
                                                <ComparisonTable>
                                                    <ComparisonHeader>
                                                        <span>Check</span>
                                                        <span>Expected</span>
                                                        <span>Actual</span>
                                                    </ComparisonHeader>
                                                    {outcome.comparisons.map((comparison, index) => (
                                                        <ComparisonRow
                                                            key={`${comparison.label}-${index}`}
                                                            $ok={comparison.ok}
                                                        >
                                                            <span>{comparison.label}</span>
                                                            <code>{comparison.expected}</code>
                                                            <code>{comparison.actual}</code>
                                                        </ComparisonRow>
                                                    ))}
                                                </ComparisonTable>
                                            )}
                                        </OutcomeCard>
                                    ))}
                                </OutcomeList>
                            </DrawerSection>

                            <DrawerSection>
                                <details>
                                    <RawSummary>Raw event context JSON</RawSummary>
                                    <RawJson>{JSON.stringify(selectedEvent.contextPreview, null, 2)}</RawJson>
                                </details>
                            </DrawerSection>
                        </DrawerBody>
                    </DrawerPanel>
                </DrawerLayer>
            )}

            {editingRule && draftRuleDefinition && (
                <DrawerLayer>
                    <DrawerBackdrop onClick={closeRuleEditor} />
                    <DrawerPanel $wide={isIntegratedEditorWide}>
                        <DrawerHeader>
                            <div>
                                <DrawerTitle>Edit {editingRule.id}</DrawerTitle>
                                <DrawerSubtitle>
                                    {editingRule.name} | {IMPLEMENTATION_LABELS[draftRuleDefinition.implementation]}
                                </DrawerSubtitle>
                            </div>
                            <CloseButton
                                type="button"
                                aria-label="Close rule editor"
                                onClick={closeRuleEditor}
                            >
                                x
                            </CloseButton>
                        </DrawerHeader>

                        <DrawerBody>
                            <DrawerSection>
                                <DrawerSectionTitle>AppGlue Rule Object</DrawerSectionTitle>
                                <EditorGrid>
                                    <EditorField>
                                        <FilterLabel htmlFor="rule-implementation">Object type</FilterLabel>
                                        <Select
                                            id="rule-implementation"
                                            value={draftRuleDefinition.implementation}
                                            onChange={(event) =>
                                                updateDraft((definition) => ({
                                                    ...definition,
                                                    implementation: event.target.value as RuleLogicImplementation,
                                                }))
                                            }
                                        >
                                            {IMPLEMENTATION_OPTIONS.map((implementation) => (
                                                <option
                                                    key={implementation}
                                                    value={implementation}
                                                >
                                                    {IMPLEMENTATION_LABELS[implementation]}
                                                </option>
                                            ))}
                                        </Select>
                                    </EditorField>
                                    <EditorField>
                                        <FilterLabel htmlFor="rule-trigger">Trigger</FilterLabel>
                                        <Select
                                            id="rule-trigger"
                                            value={draftRuleDefinition.trigger}
                                            onChange={(event) =>
                                                updateDraft((definition) => ({
                                                    ...definition,
                                                    trigger: event.target.value as DecisionEventType,
                                                }))
                                            }
                                        >
                                            {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                                                <option
                                                    key={value}
                                                    value={value}
                                                >
                                                    {label}
                                                </option>
                                            ))}
                                        </Select>
                                    </EditorField>
                                    <EditorField>
                                        <FilterLabel htmlFor="rule-source-table">Source table</FilterLabel>
                                        <Input
                                            id="rule-source-table"
                                            value={draftRuleDefinition.sourceTable}
                                            onChange={(event) =>
                                                updateDraft((definition) => ({
                                                    ...definition,
                                                    sourceTable: event.target.value,
                                                }))
                                            }
                                        />
                                    </EditorField>
                                </EditorGrid>
                                <OutcomeMeta>
                                    Integration:{" "}
                                    <code>{describeRuleIntegrationStatus(draftRuleDefinition.integrationStatus)}</code>{" "}
                                    | Target AppGlue object: <code>{draftRuleDefinition.targetAppGlueObjectLabel}</code>
                                    . This
                                    {draftRuleDefinition.integrationStatus === "demo_only"
                                        ? " rule is not native AppGlue-backed until an adapter is registered and guardrail tests prove storage, runtime, and editor integration."
                                        : " rule is backed by a registered adapter and must pass storage, runtime, and editor guardrails."}
                                </OutcomeMeta>
                            </DrawerSection>

                            {isEditingIntegratedRule ? (
                                <>
                                    <IntegratedRuleEditorHost
                                        ruleDefinition={draftRuleDefinition}
                                        config={draftIntegratedRuleConfig}
                                        onStorageRefresh={refreshDraftIntegratedRuleStorage}
                                        onCancel={closeRuleEditor}
                                        renderLargeEditorContext={() => (
                                            <IntegratedValidationPanel
                                                workbenchView={workbenchView}
                                                draftRuleDefinition={draftRuleDefinition}
                                                onOutcomeRoutingChange={updateOutcomeRouting}
                                                onSimplifiedLogicEdit={updateSimplifiedLogic}
                                            />
                                        )}
                                    />
                                    <IntegratedValidationPanel
                                        workbenchView={workbenchView}
                                        draftRuleDefinition={draftRuleDefinition}
                                        testIds={INTEGRATED_VALIDATION_TEST_IDS}
                                        onOutcomeRoutingChange={updateOutcomeRouting}
                                        onSimplifiedLogicEdit={updateSimplifiedLogic}
                                    />

                                    {workbenchView.canAskAppGlue && (
                                        <DrawerSection>
                                            <DrawerSectionTitle>Ask AppGlue</DrawerSectionTitle>
                                            <TextArea
                                                value={askPrompt}
                                                onChange={(event) => setAskPrompt(event.target.value)}
                                                placeholder="Describe a rule to draft..."
                                            />
                                            <RuleActions>
                                                <SecondaryButton
                                                    type="button"
                                                    onClick={applyAskSuggestion}
                                                >
                                                    Draft from prompt
                                                </SecondaryButton>
                                            </RuleActions>
                                            {askSuggestion && (
                                                <SuggestionBox>
                                                    <StrongText>{askSuggestion.title}</StrongText>
                                                    <OutcomeText>{askSuggestion.explanation}</OutcomeText>
                                                    <OutcomeMeta>
                                                        Mode: <code>{askSuggestion.mode}</code> | Affected rows:{" "}
                                                        <code>{formatInteger(askSuggestion.affectedRowCount)}</code>
                                                    </OutcomeMeta>
                                                    {askSuggestion.command ? (
                                                        <details>
                                                            <RawSummary>AppGlue command</RawSummary>
                                                            <RawJson>{JSON.stringify(askSuggestion.command, null, 2)}</RawJson>
                                                        </details>
                                                    ) : (
                                                        <OutcomeMeta>Proposal only; no executable command was applied.</OutcomeMeta>
                                                    )}
                                                </SuggestionBox>
                                            )}
                                        </DrawerSection>
                                    )}
                                </>
                            ) : (
                                <>
                                    <DrawerSection data-testid="electronics-demo-rule-condition-editor">
                                        <DrawerSectionTitle>Conditions</DrawerSectionTitle>
                                        <ConditionEditorList>
                                            {draftRuleDefinition.conditions.map((condition, index) => (
                                                <ConditionEditorRow key={condition.id}>
                                                    <Input
                                                        aria-label={`Condition ${index + 1} field`}
                                                        value={condition.field}
                                                        onChange={(event) =>
                                                            updateDraft((definition) => ({
                                                                ...definition,
                                                                conditions: definition.conditions.map(
                                                                    (candidate, candidateIndex) =>
                                                                        candidateIndex === index
                                                                            ? {
                                                                                  ...candidate,
                                                                                  field: normalizeRuleField(
                                                                                      event.target.value,
                                                                                  ),
                                                                              }
                                                                            : candidate,
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                    <Select
                                                        aria-label={`Condition ${index + 1} operator`}
                                                        value={condition.operator}
                                                        onChange={(event) =>
                                                            updateDraft((definition) => ({
                                                                ...definition,
                                                                conditions: definition.conditions.map(
                                                                    (candidate, candidateIndex) =>
                                                                        candidateIndex === index
                                                                            ? {
                                                                                  ...candidate,
                                                                                  operator: event.target
                                                                                      .value as RuleLogicOperator,
                                                                                  value: parseConditionValue(
                                                                                      event.target
                                                                                          .value as RuleLogicOperator,
                                                                                      conditionValueText(candidate),
                                                                                  ),
                                                                              }
                                                                            : candidate,
                                                                ),
                                                            }))
                                                        }
                                                    >
                                                        {OPERATOR_OPTIONS.map((operator) => (
                                                            <option
                                                                key={operator}
                                                                value={operator}
                                                            >
                                                                {operator}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    <Input
                                                        aria-label={`Condition ${index + 1} value`}
                                                        value={
                                                            condition.operator === "lookup"
                                                                ? (condition.lookupTable ?? "")
                                                                : conditionValueText(condition)
                                                        }
                                                        disabled={condition.operator === "exists"}
                                                        onChange={(event) =>
                                                            updateDraft((definition) => ({
                                                                ...definition,
                                                                conditions: definition.conditions.map(
                                                                    (candidate, candidateIndex) =>
                                                                        candidateIndex === index
                                                                            ? candidate.operator === "lookup"
                                                                                ? {
                                                                                      ...candidate,
                                                                                      lookupTable: event.target.value,
                                                                                  }
                                                                                : {
                                                                                      ...candidate,
                                                                                      value: parseConditionValue(
                                                                                          candidate.operator,
                                                                                          event.target.value,
                                                                                      ),
                                                                                  }
                                                                            : candidate,
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </ConditionEditorRow>
                                            ))}
                                        </ConditionEditorList>
                                        <SecondaryButton
                                            type="button"
                                            onClick={() =>
                                                updateDraft((definition) => ({
                                                    ...definition,
                                                    conditions: [
                                                        ...definition.conditions,
                                                        {
                                                            id: `condition-${definition.conditions.length + 1}`,
                                                            field: "category",
                                                            operator: "==",
                                                            value: "",
                                                        },
                                                    ],
                                                }))
                                            }
                                        >
                                            Add condition
                                        </SecondaryButton>
                                    </DrawerSection>

                                    <DrawerSection data-testid="electronics-demo-rule-required-fields-editor">
                                        <DrawerSectionTitle>Required Fields</DrawerSectionTitle>
                                        <TextArea
                                            value={requirementsText(draftRuleDefinition)}
                                            onChange={(event) =>
                                                updateDraft((definition) => {
                                                    const fields = parseFieldList(event.target.value);
                                                    return {
                                                        ...definition,
                                                        requirements:
                                                            fields.length > 0
                                                                ? [{ id: "required-fields", fields }]
                                                                : [],
                                                    };
                                                })
                                            }
                                        />
                                    </DrawerSection>

                                    <DrawerSection data-testid="electronics-demo-rule-actions-editor">
                                        <DrawerSectionTitle>Actions</DrawerSectionTitle>
                                        <TextArea
                                            value={actionText(draftRuleDefinition)}
                                            onChange={(event) =>
                                                updateDraft((definition) => {
                                                    const lines = event.target.value
                                                        .split("\n")
                                                        .map((line) => line.trim())
                                                        .filter(Boolean);
                                                    return {
                                                        ...definition,
                                                        actions: lines.map((line, index) => ({
                                                            id: definition.actions[index]?.id ?? `action-${index + 1}`,
                                                            label: line,
                                                            outputQueue: definition.actions[index]?.outputQueue,
                                                        })),
                                                    };
                                                })
                                            }
                                        />
                                    </DrawerSection>

                                    <DrawerSection>
                                        <DrawerSectionTitle>Validation Preview</DrawerSectionTitle>
                                        <PreviewGrid>
                                            <MetricCard>
                                                <MetricLabel>Pass</MetricLabel>
                                                <MetricValue>
                                                    {formatInteger(
                                                        draftRuleCounts?.pass ?? currentRuleCounts?.pass ?? 0,
                                                    )}
                                                </MetricValue>
                                                <MetricDelta>
                                                    was {formatInteger(currentRuleCounts?.pass ?? 0)}
                                                </MetricDelta>
                                            </MetricCard>
                                            <MetricCard>
                                                <MetricLabel>Flagged</MetricLabel>
                                                <MetricValue $tone="warning">
                                                    {formatInteger(
                                                        draftRuleCounts?.flagged ?? currentRuleCounts?.flagged ?? 0,
                                                    )}
                                                </MetricValue>
                                                <MetricDelta>
                                                    was {formatInteger(currentRuleCounts?.flagged ?? 0)}
                                                </MetricDelta>
                                            </MetricCard>
                                            <MetricCard>
                                                <MetricLabel>Rejected</MetricLabel>
                                                <MetricValue $tone="danger">
                                                    {formatInteger(
                                                        draftRuleCounts?.rejected ?? currentRuleCounts?.rejected ?? 0,
                                                    )}
                                                </MetricValue>
                                                <MetricDelta>
                                                    was {formatInteger(currentRuleCounts?.rejected ?? 0)}
                                                </MetricDelta>
                                            </MetricCard>
                                        </PreviewGrid>
                                        <LogicBox>
                                            <LogicTitle>Generated simplified logic</LogicTitle>
                                            <LogicList>
                                                {renderDisplayRuleLogicLines(
                                                    draftRuleDefinition,
                                                    undefined,
                                                    priceToleranceRules,
                                                ).map((line) => (
                                                    <li key={line}>
                                                        <code>{line}</code>
                                                    </li>
                                                ))}
                                            </LogicList>
                                        </LogicBox>
                                    </DrawerSection>

                                    <DrawerSection>
                                        <DrawerSectionTitle>Ask AppGlue</DrawerSectionTitle>
                                        <TextArea
                                            value={askPrompt}
                                            onChange={(event) => setAskPrompt(event.target.value)}
                                            placeholder="Describe a rule to draft..."
                                        />
                                        <RuleActions>
                                            <SecondaryButton
                                                type="button"
                                                onClick={applyAskSuggestion}
                                            >
                                                Draft from prompt
                                            </SecondaryButton>
                                        </RuleActions>
                                        {askSuggestion && (
                                            <SuggestionBox>
                                                <StrongText>{askSuggestion.title}</StrongText>
                                                <OutcomeText>{askSuggestion.explanation}</OutcomeText>
                                                <OutcomeMeta>
                                                    Mode: <code>{askSuggestion.mode}</code> | Affected rows:{" "}
                                                    <code>{formatInteger(askSuggestion.affectedRowCount)}</code>
                                                </OutcomeMeta>
                                                {askSuggestion.command ? (
                                                    <details>
                                                        <RawSummary>AppGlue command</RawSummary>
                                                        <RawJson>{JSON.stringify(askSuggestion.command, null, 2)}</RawJson>
                                                    </details>
                                                ) : (
                                                    <OutcomeMeta>Proposal only; no executable command was applied.</OutcomeMeta>
                                                )}
                                            </SuggestionBox>
                                        )}
                                    </DrawerSection>
                                </>
                            )}

                            <DrawerFooter>
                                <GhostButton
                                    type="button"
                                    onClick={resetDraftRule}
                                >
                                    Reset this rule
                                </GhostButton>
                                <FooterActions>
                                    <GhostButton
                                        type="button"
                                        onClick={closeRuleEditor}
                                    >
                                        Cancel
                                    </GhostButton>
                                    <PrimaryButton
                                        type="button"
                                        disabled={isEditingIntegratedRule && !draftIntegratedRuleConfig}
                                        onClick={saveDraftRule}
                                    >
                                        Save rule
                                    </PrimaryButton>
                                </FooterActions>
                            </DrawerFooter>
                        </DrawerBody>
                    </DrawerPanel>
                </DrawerLayer>
            )}
        </DemoSurface>
    );
};

const DemoSurface = styled.div`
    min-height: 100%;
    background: #f4f6fa;
    color: #111827;
    padding: 0 20px 24px;
    overflow: auto;
`;

const DemoHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 0 18px;
`;

const HeaderIdentity = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    background: #1f6feb;
    color: #ffffff;
    font-size: 13px;
    font-weight: 900;
`;

const Title = styled.h1`
    margin: 0;
    color: #111827;
    font-size: 22px;
    line-height: 28px;
    letter-spacing: 0;
`;

const Subtitle = styled.div`
    margin-top: 2px;
    color: #667085;
    font-size: 13px;
`;

const HeaderActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
`;

const HeaderBadge = styled.span`
    border: 1px solid #cfd7e3;
    background: #ffffff;
    border-radius: 8px;
    color: #344054;
    font-size: 13px;
    font-weight: 700;
    padding: 7px 10px;
`;

const DemoGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(360px, 0.42fr) minmax(680px, 1fr);
    gap: 24px;
    align-items: start;

    @media (max-width: 1180px) {
        grid-template-columns: 1fr;
    }
`;

const LeftColumn = styled.div`
    display: grid;
    gap: 18px;
`;

const RightColumn = styled.div`
    display: grid;
    gap: 18px;
`;

const Panel = styled.section`
    border: 1px solid #cfd7e3;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
`;

const PanelHeader = styled.div`
    padding: 16px 18px 0;
`;

const PanelHeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px 0;
`;

const PanelTitle = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 16px;
    line-height: 22px;
    font-weight: 800;
`;

const PanelNote = styled.div`
    margin-top: 2px;
    color: #667085;
    font-size: 12px;
`;

const RulesetControl = styled.div`
    display: grid;
    gap: 9px;
    padding: 14px 18px 18px;
`;

const RulesetLabel = styled.label`
    color: #667085;
    font-size: 12px;
    font-weight: 700;
`;

const RulesetRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
`;

const RulesetValue = styled.div`
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 8px;
    color: #111827;
    font-size: 14px;
    padding: 10px 12px;
`;

const RulesetNote = styled.div`
    color: #667085;
    font-size: 12px;
    line-height: 17px;
`;

const SecondaryButton = styled.button`
    border: 1px solid #cfd7e3;
    background: #ffffff;
    border-radius: 8px;
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    padding: 8px 12px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);

    &:hover {
        background: #f8fafc;
    }
`;

const PrimaryButton = styled.button`
    border: 1px solid #1f6feb;
    background: #1f6feb;
    border-radius: 8px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 800;
    padding: 8px 12px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);

    &:hover {
        background: #175ec5;
    }

    &:disabled {
        border-color: #cfd7e3;
        background: #e5e7eb;
        color: #667085;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const GhostButton = styled.button`
    border: 1px solid #d9e0ea;
    background: #f8fafc;
    border-radius: 8px;
    color: #344054;
    font-size: 13px;
    font-weight: 700;
    padding: 8px 12px;
    cursor: pointer;
`;

const RuleToolbar = styled.div`
    display: flex;
    gap: 8px;
`;

const RuleList = styled.div`
    max-height: calc(100vh - 300px);
    overflow: auto;
    margin: 14px 18px 18px;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #ffffff;
`;

const RuleItem = styled.div<{ $selected: boolean }>`
    border-top: 1px solid #d9e0ea;
    border-left: 4px solid ${(props) => (props.$selected ? "#1f6feb" : "transparent")};

    &:first-child {
        border-top: 0;
    }
`;

const RuleButton = styled.button`
    width: 100%;
    border: 0;
    background: transparent;
    padding: 12px 16px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f8fafc;
    }
`;

const RuleTopline = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const RuleId = styled.div`
    color: #667085;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
`;

const RuleBadges = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
`;

const RuleName = styled.div`
    margin-top: 7px;
    color: #111827;
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
`;

const RuleMeta = styled.div`
    margin-top: 4px;
    color: #667085;
    font-size: 12px;
`;

const RuleExpansion = styled.div`
    border-top: 1px solid #d9e0ea;
    background: #f8fafc;
    padding: 14px 18px;
`;

const RuleDescription = styled.div`
    color: #475467;
    font-size: 13px;
    line-height: 19px;
`;

const TriggerLine = styled.div`
    margin-top: 9px;
    color: #667085;
    font-size: 12px;

    code {
        color: #111827;
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
    }
`;

const LogicBox = styled.div`
    margin-top: 12px;
    border: 1px solid #111827;
    background: #ffffff;
    border-radius: 8px;
    padding: 12px 14px;
`;

const LogicTitle = styled.div`
    color: #667085;
    font-size: 12px;
    font-weight: 800;
`;

const LogicWarning = styled.div`
    margin-top: 8px;
    color: #9f580a;
    font-size: 12px;
    line-height: 18px;
`;

const LogicList = styled.ul`
    margin: 8px 0 0;
    padding-left: 20px;
    display: grid;
    gap: 5px;
    color: #111827;
    font-size: 12px;

    code {
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
    }
`;

const EditableLogicList = styled.ul`
    margin: 8px 0 0;
    padding-left: 20px;
    display: grid;
    gap: 7px;
    color: #111827;
    font-size: 12px;

    code {
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
    }
`;

const EditableLogicLine = styled.li`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
`;

const LogicKeyword = styled.code`
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
`;

const LogicOperator = styled.code`
    min-width: 18px;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    text-align: center;
`;

const LogicSegmentSelect = styled.select`
    width: auto;
    min-width: 150px;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 6px;
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    padding: 6px 8px;
`;

const LogicSegmentInput = styled.input`
    min-width: 170px;
    flex: 1;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 6px;
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    padding: 6px 8px;
`;

const ActionRoutingBox = styled.div`
    min-width: 0;
    margin-top: 10px;
    overflow: hidden;
    border: 1px solid #d9e0ea;
    background: #ffffff;
    border-radius: 8px;
    padding: 10px;
`;

const ActionRoutingRows = styled.div`
    display: grid;
    gap: 6px;
    margin-top: 6px;
`;

const ActionRoutingRow = styled.div`
    display: grid;
    grid-template-columns: 48px minmax(86px, 0.7fr) minmax(150px, 1.1fr) minmax(130px, 0.9fr);
    gap: 5px 6px;
    align-items: end;
    border-top: 1px solid #edf1f7;
    padding-top: 6px;

    &:first-child {
        border-top: 0;
        padding-top: 0;
    }

    @media (max-width: 760px) {
        grid-template-columns: 42px minmax(0, 1fr) minmax(0, 1fr);
    }
`;

const RouteWhen = styled.div`
    color: #667085;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    font-weight: 800;
    line-height: 28px;
    align-self: center;
    grid-row: span 2;
`;

const ActionRoutingField = styled.label<{ $span?: number }>`
    min-width: 0;
    display: grid;
    gap: 2px;
    grid-column: span ${(props) => props.$span ?? 1};

    @media (max-width: 760px) {
        grid-column: span 1;
    }
`;

const ActionRoutingLabel = styled.span`
    color: #667085;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.03em;
    line-height: 11px;
    text-transform: uppercase;
`;

const ActionRoutingSelect = styled.select`
    width: 100%;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    box-sizing: border-box;
    color: #111827;
    height: 30px;
    min-width: 0;
    border-radius: 6px;
    font-size: 12px;
    padding: 5px 8px;
`;

const ActionRoutingInput = styled.input`
    width: 100%;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    box-sizing: border-box;
    color: #111827;
    height: 30px;
    min-width: 0;
    border-radius: 6px;
    font-size: 12px;
    padding: 5px 8px;
`;

const RuleActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const CategoryBadge = styled.span`
    border: 1px solid #cfd7e3;
    background: #f1f5f9;
    border-radius: 999px;
    color: #475467;
    font-size: 11px;
    font-weight: 800;
    padding: 3px 8px;
`;

const StatusBadge = styled.span<{ $tone: "success" | "warning" | "danger" | "neutral" }>`
    border: 1px solid
        ${(props) =>
            props.$tone === "success"
                ? "#a7f3d0"
                : props.$tone === "warning"
                  ? "#fed7aa"
                  : props.$tone === "danger"
                    ? "#fecdd3"
                    : "#cfd7e3"};
    background: ${(props) =>
        props.$tone === "success"
            ? "#ecfdf3"
            : props.$tone === "warning"
              ? "#fff7ed"
              : props.$tone === "danger"
                ? "#fff1f2"
                : "#f1f5f9"};
    color: ${(props) =>
        props.$tone === "success"
            ? "#047857"
            : props.$tone === "warning"
              ? "#b45309"
              : props.$tone === "danger"
                ? "#be123c"
                : "#475467"};
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
    padding: 2px 8px;
`;

const FilterChip = styled.span`
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 999px;
    color: #475467;
    font-size: 12px;
    font-weight: 700;
    padding: 5px 10px;
`;

const EvaluationGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    padding: 16px 18px 0;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const MetricCard = styled.div`
    border: 1px solid #111827;
    background: #f8fafc;
    border-radius: 8px;
    padding: 14px 16px;
`;

const MetricLabel = styled.div`
    color: #667085;
    font-size: 13px;
`;

const MetricValue = styled.div<{ $tone?: "warning" | "danger" }>`
    margin-top: 6px;
    color: ${(props) => (props.$tone === "warning" ? "#b45309" : props.$tone === "danger" ? "#be123c" : "#111827")};
    font-size: 28px;
    line-height: 34px;
    font-weight: 800;
`;

const MetricDelta = styled.div`
    margin-top: 3px;
    color: #667085;
    font-size: 12px;
`;

const FilterGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(140px, 0.8fr) minmax(220px, 1.2fr) minmax(160px, 0.8fr) minmax(180px, 1fr);
    gap: 12px;
    padding: 18px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const FilterField = styled.div`
    display: grid;
    gap: 5px;
`;

const FilterLabel = styled.label`
    color: #667085;
    font-size: 12px;
`;

const Select = styled.select`
    width: 100%;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 8px;
    color: #111827;
    font-size: 14px;
    padding: 10px 11px;
    box-sizing: border-box;
`;

const Input = styled.input`
    width: 100%;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 8px;
    color: #111827;
    font-size: 14px;
    padding: 10px 11px;
    box-sizing: border-box;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 86px;
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    border-radius: 8px;
    color: #111827;
    font-size: 14px;
    line-height: 20px;
    padding: 10px 11px;
    box-sizing: border-box;
    resize: vertical;
`;

const EventTable = styled.div`
    margin: 14px 18px 18px;
    border: 1px solid #111827;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
`;

const EventHeader = styled.div`
    display: grid;
    grid-template-columns: 1.3fr 1.4fr 2.1fr 0.6fr 0.7fr;
    gap: 12px;
    border-bottom: 1px solid #111827;
    background: #f1f5f9;
    color: #667085;
    font-size: 13px;
    font-weight: 700;
    padding: 12px 16px;
`;

const EventViewport = styled.div`
    height: ${EVENT_VIEWPORT_HEIGHT}px;
    overflow: auto;
`;

const EventRows = styled.div`
    position: relative;
`;

const EventRow = styled.button<{ $tone: "success" | "warning" | "danger" | "neutral"; $selected: boolean }>`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    min-height: ${ROW_HEIGHT}px;
    display: grid;
    grid-template-columns: 1.3fr 1.4fr 2.1fr 0.6fr 0.7fr;
    gap: 12px;
    align-items: center;
    border: 0;
    border-bottom: 1px solid #111827;
    border-left: 4px solid
        ${(props) =>
            props.$tone === "success"
                ? "#10b981"
                : props.$tone === "warning"
                  ? "#f59e0b"
                  : props.$tone === "danger"
                    ? "#e11d48"
                    : "#cfd7e3"};
    background: ${(props) =>
        props.$tone === "success"
            ? "#ecfdf3"
            : props.$tone === "warning"
              ? "#fffbeb"
              : props.$tone === "danger"
                ? "#fff1f2"
                : "#ffffff"};
    box-shadow: ${(props) => (props.$selected ? "inset 0 0 0 2px rgba(31, 111, 235, 0.35)" : "none")};
    color: #111827;
    cursor: pointer;
    padding: 9px 12px;
    text-align: left;

    &:hover {
        filter: brightness(0.985);
    }
`;

const EventCell = styled.div`
    min-width: 0;
    display: grid;
    gap: 2px;
`;

const EventType = styled.div`
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MutedMono = styled.div`
    color: #667085;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StrongText = styled.div`
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeadlineCell = styled.div`
    color: #475467;
    font-size: 12px;
    line-height: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const RulePill = styled.span`
    display: inline-flex;
    width: fit-content;
    border: 1px solid #cfd7e3;
    background: #ffffff;
    border-radius: 999px;
    color: #475467;
    font-family: "Source Code Pro", monospace;
    font-size: 11px;
    font-weight: 800;
    padding: 4px 8px;
`;

const EmptyState = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    padding: 24px;
    color: #667085;
    font-size: 14px;
`;

const DrawerLayer = styled.div`
    position: fixed;
    inset: 0;
    z-index: 70;
`;

const DrawerBackdrop = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(17, 24, 39, 0.24);
`;

const DrawerPanel = styled.aside<{ $wide?: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    width: ${(props) => (props.$wide ? "min(1180px, 100vw)" : "min(720px, 100vw)")};
    height: 100%;
    border-left: 1px solid #cfd7e3;
    background: #ffffff;
    box-shadow: -8px 0 30px rgba(16, 24, 40, 0.18);
    overflow: auto;
`;

const DrawerHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    border-bottom: 1px solid #d9e0ea;
    background: #ffffff;
    padding: 22px 24px;
`;

const DrawerTitle = styled.div`
    color: #111827;
    font-size: 20px;
    font-weight: 800;
`;

const DrawerSubtitle = styled.div`
    margin-top: 4px;
    color: #667085;
    font-size: 13px;
`;

const CloseButton = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid #cfd7e3;
    background: #ffffff;
    border-radius: 8px;
    color: #475467;
    font-size: 18px;
    font-weight: 800;
    cursor: pointer;
`;

const DrawerBody = styled.div`
    display: grid;
    gap: 18px;
    padding: 20px 24px 28px;
`;

const EditorGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const EditorField = styled.div`
    display: grid;
    gap: 5px;
`;

const ConditionEditorList = styled.div`
    display: grid;
    gap: 8px;
    margin-bottom: 10px;
`;

const ConditionEditorRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 120px minmax(0, 1fr);
    gap: 8px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const PreviewGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const SuggestionBox = styled.div`
    display: grid;
    gap: 8px;
    margin-top: 12px;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #f8fafc;
    padding: 12px;
`;

const DrawerFooter = styled.div`
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    border-top: 1px solid #d9e0ea;
    background: #ffffff;
    padding: 14px 0 0;
`;

const FooterActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const DetailSummary = styled.div`
    display: grid;
    gap: 0;
    overflow: hidden;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #ffffff;
`;

const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    border-top: 1px solid #d9e0ea;
    padding: 10px 12px;

    &:first-child {
        border-top: 0;
    }
`;

const SummaryLabel = styled.div`
    color: #667085;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
`;

const SummaryValue = styled.div`
    color: #111827;
    font-size: 13px;
    line-height: 18px;
`;

const MonoValue = styled.div`
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
    word-break: break-word;
`;

const DrawerSection = styled.section`
    display: grid;
    gap: 10px;
`;

const DrawerSectionTitle = styled.h3`
    margin: 0;
    color: #111827;
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
`;

const ActionBox = styled.div`
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #f8fafc;
    padding: 12px;
`;

const ContextTable = styled.div`
    overflow: hidden;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
`;

const ContextRow = styled.div`
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 12px;
    border-top: 1px solid #d9e0ea;
    padding: 10px 12px;

    &:first-child {
        border-top: 0;
    }
`;

const ContextKey = styled.div`
    color: #667085;
    font-size: 12px;
    text-transform: capitalize;
`;

const ContextValue = styled.div`
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
    word-break: break-word;
`;

const OutcomeList = styled.div`
    display: grid;
    gap: 10px;
`;

const OutcomeCard = styled.div`
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #ffffff;
    padding: 12px;
`;

const OutcomeHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
`;

const OutcomeRule = styled.div`
    color: #667085;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
`;

const OutcomeName = styled.div`
    margin-top: 3px;
    color: #111827;
    font-size: 14px;
    font-weight: 800;
`;

const OutcomeText = styled.div`
    margin-top: 9px;
    color: #344054;
    font-size: 13px;
    line-height: 19px;
`;

const OutcomeMeta = styled.div`
    margin-top: 7px;
    color: #667085;
    font-size: 12px;

    code {
        color: #111827;
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
    }
`;

const ComparisonTable = styled.div`
    margin-top: 10px;
    overflow: hidden;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
`;

const ComparisonHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    background: #f1f5f9;
    color: #667085;
    font-size: 12px;
    font-weight: 800;
    padding: 8px 10px;
`;

const ComparisonRow = styled.div<{ $ok?: boolean }>`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    border-top: 1px solid #d9e0ea;
    background: ${(props) => (props.$ok === false ? "#fff1f2" : "#ffffff")};
    color: #344054;
    font-size: 12px;
    padding: 8px 10px;

    code {
        color: ${(props) => (props.$ok === false ? "#be123c" : "#111827")};
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
        white-space: pre-wrap;
        word-break: break-word;
    }
`;

const RawSummary = styled.summary`
    cursor: pointer;
    color: #667085;
    font-size: 13px;
    font-weight: 800;
`;

const RawJson = styled.pre`
    margin: 10px 0 0;
    overflow: auto;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #f8fafc;
    color: #111827;
    font-family: "Source Code Pro", monospace;
    font-size: 12px;
    line-height: 17px;
    padding: 12px;
`;
